// The company's operations in numbers (cost, schedule, quality for construction; jobs, boats, parts for Marine), counted
// live by the server from its own records. One source, shown in three places:
// - Home: "Operations overview", the numbers only (it used to be called the VERA Brain);
// - Files → Knowledge base: the layers of what VERA understands about the company (the framework stays out of Home);
// - Settings: the owner's demo firm loader, for showing VERA Studio to others.
// Words come from the server in the industry's language, or in English and are shown through the screens' dictionary.
import { useCallback, useEffect, useState } from "react";
import { crm, type BrainNow } from "../lib/crm";
import { T } from "../lib/i18n";
import { Orb, useStudio } from "./ui";

function useBrain(stamp?: string) {
  const { getToken } = useStudio();
  const [brain, setBrain] = useState<BrainNow | null | undefined>(undefined);
  const [demoState, setDemoState] = useState<{ present: number; missing: number } | null>(null);
  const load = useCallback(() => {
    crm.brain(getToken).then((r) => { setBrain(r.brain); setDemoState(r.demo ?? null); }).catch(() => setBrain(null));
  }, [getToken]);
  useEffect(load, [load, stamp]);
  return { brain, demoState, load };
}

/** Home: the operations in numbers, nothing else. */
export function BrainCard({ stamp }: { stamp?: string }) {
  const { brain } = useBrain(stamp);
  if (!brain) return null;
  return (
    <section className="card brain">
      <div className="row between">
        <div className="row" style={{ gap: 8 }}><Orb size={16} /><h2>{T("Operations overview")}</h2></div>
        <span className="faint small">{T("Counted from your records, live")}</span>
      </div>
      <div className="grid g3 brain-outcomes">
        {brain.outcomes.map((o) => (
          <div className="brain-outcome" key={o.id}>
            <b>{T(o.name)}</b>
            <div className="faint small">{T(o.about)}</div>
            <div className="list" style={{ marginTop: 8 }}>
              {o.signals.map((s) => (
                <div className="li brain-signal" key={s.label}>
                  <span className="grow">{T(s.label)}</span>
                  <b className={`num ${s.alert ? "alert" : ""}`}>{s.shown}</b>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Files → Knowledge base: the layers of what VERA understands about the company; each opens a conversation about it. */
export function BrainLayers() {
  const { go } = useStudio();
  const { brain } = useBrain();
  if (!brain?.layers.length) return null;
  return (
    <section className="card" style={{ padding: 16 }}>
      <b>{T("How VERA understands your company")}</b>
      <p className="faint" style={{ margin: "4px 0 10px", fontSize: 12.5 }}>{T("The layers VERA organises everything she learns about your company into, with how many of your records each one holds. Open one to talk about it.")}</p>
      <div className="brain-layers" aria-label={T("Intelligence layers")}>
        {brain.layers.map((l) => (
          <button key={l.id} className="brain-layer" title={T(l.about)} onClick={() => go("workspace", { ask: T("What do you see on the layer:") + ` ${T(l.name)}`, fresh: true, title: T(l.name) })}>
            <span className="faint">{String(l.n).padStart(2, "0")}</span> {T(l.name)}{l.records ? <span className="faint"> · {l.records}</span> : null}
          </button>
        ))}
      </div>
    </section>
  );
}

/** Settings: the pack's demo firm, for an owner showing VERA Studio to others. Hidden once there's nothing to add. */
export function PackDemoLoader({ onChanged }: { onChanged?: () => void }) {
  const { getToken, toast } = useStudio();
  const { brain, demoState, load } = useBrain();
  const [loading, setLoading] = useState(false);
  if (!brain) return null;
  const empty = brain.layers.every((l) => l.records === 0);
  if (!(empty || (demoState && demoState.present > 0 && demoState.missing > 0))) return null;
  const loadDemo = async () => {
    setLoading(true);
    try { const run = await crm.importDemo(getToken); toast(run.summary); onChanged?.(); load(); }
    catch (e) { toast(e instanceof Error ? e.message : T("That didn't work. Please try again.")); }
    finally { setLoading(false); }
  };
  return (
    <div className="notice calm row between">
      <span className="mute">{empty ? T("Your workspace is empty. Start from a demo firm with realistic projects, then make it yours.") : T("The demo firm has new records you don't have yet.")}</span>
      <button className="btn" disabled={loading} onClick={() => void loadDemo()}>{loading ? T("Loading…") : empty ? T("Load the demo firm") : T("Add the new demo records")}</button>
    </div>
  );
}
